import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { Tables } from '@limo/database'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useToast } from '@/hooks/use-toast'
import { Loader2, AlertCircle } from 'lucide-react'
import { checkMinimumHourOverlap } from '@/lib/pricing/overlap-validation'

type MinimumHourAdjustment = Tables<'minimum_hour_adjustments'>

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
] as const

const minimumHourSchema = z
  .object({
    name: z.string().min(1, 'Name is required').max(100, 'Name too long'),
    description: z.string().max(500, 'Description too long').optional(),
    start_date: z.string().optional(),
    end_date: z.string().optional(),
    all_day: z.boolean().default(true),
    start_time: z.string().regex(/^\d{2}:\d{2}$/, 'Invalid time format').optional(),
    end_time: z.string().regex(/^\d{2}:\d{2}$/, 'Invalid time format').optional(),
    days_of_week: z
      .array(z.number())
      .min(1, 'Select at least one day')
      .max(7),
    minimum_hours: z.coerce
      .number()
      .min(0.5, 'Must be at least 0.5 hours')
      .max(24, 'Maximum 24 hours'),
    is_active: z.boolean().default(true),
    priority: z.coerce.number().min(0).default(0),
    apply_to_all_vehicles: z.boolean().default(true),
    vehicle_ids: z.array(z.string()).optional(),
  })
  .refine(
    (data) => {
      if (!data.all_day && data.start_time && data.end_time) {
        return data.start_time !== data.end_time
      }
      return true
    },
    {
      message: 'Start and end time must be different',
      path: ['end_time'],
    }
  )

type MinimumHourFormData = z.infer<typeof minimumHourSchema>

interface MinimumHourAdjustmentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  adjustment?: MinimumHourAdjustment | null
  accountId: string
  vehicles: Array<{ id: string; name: string }>
  existingRules: MinimumHourAdjustment[]
  onSuccess?: () => void
}

export function MinimumHourAdjustmentDialog({
  open,
  onOpenChange,
  adjustment,
  accountId,
  vehicles,
  existingRules,
  onSuccess,
}: MinimumHourAdjustmentDialogProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const isEditing = !!adjustment
  const [validationError, setValidationError] = useState<string | null>(null)

  const form = useForm<MinimumHourFormData>({
    resolver: zodResolver(minimumHourSchema),
    defaultValues: {
      name: '',
      description: '',
      start_date: '',
      end_date: '',
      all_day: true,
      start_time: '18:00',
      end_time: '23:00',
      days_of_week: [5, 6],
      minimum_hours: 5,
      is_active: true,
      priority: 0,
      apply_to_all_vehicles: true,
      vehicle_ids: [],
    },
  })

  // Reset form when dialog opens or adjustment changes
  useEffect(() => {
    if (open) {
      setValidationError(null)
      if (adjustment) {
        const isAllDay = adjustment.start_time === null && adjustment.end_time === null
        form.reset({
          name: adjustment.name,
          description: adjustment.description || '',
          start_date: adjustment.start_date || '',
          end_date: adjustment.end_date || '',
          all_day: isAllDay,
          start_time: adjustment.start_time ? adjustment.start_time.slice(0, 5) : '18:00',
          end_time: adjustment.end_time ? adjustment.end_time.slice(0, 5) : '23:00',
          days_of_week: adjustment.days_of_week,
          minimum_hours: adjustment.minimum_hours,
          is_active: adjustment.is_active,
          priority: adjustment.priority,
          apply_to_all_vehicles: !adjustment.vehicle_ids,
          vehicle_ids: adjustment.vehicle_ids || [],
        })
      } else {
        form.reset({
          name: '',
          description: '',
          start_date: '',
          end_date: '',
          all_day: true,
          start_time: '18:00',
          end_time: '23:00',
          days_of_week: [5, 6],
          minimum_hours: 5,
          is_active: true,
          priority: 0,
          apply_to_all_vehicles: true,
          vehicle_ids: [],
        })
      }
    }
  }, [open, adjustment, form])

  const mutation = useMutation({
    mutationFn: async (data: MinimumHourFormData) => {
      const vehicleIds = data.apply_to_all_vehicles ? null : (data.vehicle_ids || [])
      const startTime = data.all_day ? null : (data.start_time ? data.start_time + ':00' : null)
      const endTime = data.all_day ? null : (data.end_time ? data.end_time + ':00' : null)

      // Run same-type overlap validation. Price adjustments are deliberately not
      // checked here — a minimum hour rule and a price adjustment stack, so they
      // may share the same days, time window, and vehicles.
      const proposedRule = {
        id: adjustment?.id || '',
        name: data.name,
        daysOfWeek: data.days_of_week,
        startTime: data.all_day ? null : (data.start_time || null),
        endTime: data.all_day ? null : (data.end_time || null),
        vehicleIds,
        isActive: data.is_active,
        minimumHours: data.minimum_hours,
      }

      const overlapCheck = checkMinimumHourOverlap(
        proposedRule,
        existingRules.map((r) => ({
          id: r.id,
          name: r.name,
          daysOfWeek: r.days_of_week,
          startTime: r.start_time ? r.start_time.slice(0, 5) : null,
          endTime: r.end_time ? r.end_time.slice(0, 5) : null,
          vehicleIds: r.vehicle_ids,
          isActive: r.is_active,
          minimumHours: r.minimum_hours,
        })),
        isEditing ? adjustment?.id : undefined
      )

      if (overlapCheck.hasConflict) {
        throw new Error(overlapCheck.conflictDetails)
      }

      const payload = {
        account_id: accountId,
        name: data.name,
        description: data.description || null,
        start_date: data.start_date || null,
        end_date: data.end_date || null,
        start_time: startTime,
        end_time: endTime,
        days_of_week: data.days_of_week,
        minimum_hours: data.minimum_hours,
        is_active: data.is_active,
        priority: data.priority,
        vehicle_ids: vehicleIds,
      }

      if (isEditing && adjustment) {
        const { error } = await supabase
          .from('minimum_hour_adjustments')
          .update(payload)
          .eq('id', adjustment.id)

        if (error) throw error
      } else {
        const { error } = await supabase.from('minimum_hour_adjustments').insert(payload)

        if (error) throw error
      }
    },
    onSuccess: () => {
      setValidationError(null)
      queryClient.invalidateQueries({ queryKey: ['minimum-hour-adjustments', accountId] })
      toast({
        title: isEditing ? 'Rule updated' : 'Rule created',
        description: `Minimum hour rule "${form.getValues('name')}" has been ${isEditing ? 'updated' : 'created'}.`,
      })
      onOpenChange(false)
      onSuccess?.()
    },
    onError: (error) => {
      setValidationError(error.message)
    },
  })

  const selectedDays = form.watch('days_of_week')
  const applyToAllVehicles = form.watch('apply_to_all_vehicles')
  const allDay = form.watch('all_day')

  const handleDayToggle = (day: number, checked: boolean) => {
    const current = form.getValues('days_of_week')
    if (checked) {
      form.setValue('days_of_week', [...current, day].sort((a, b) => a - b), {
        shouldValidate: true,
      })
    } else {
      form.setValue(
        'days_of_week',
        current.filter((d) => d !== day),
        { shouldValidate: true }
      )
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Minimum Hour Rule' : 'Add Minimum Hour Rule'}
          </DialogTitle>
          <DialogDescription>
            Set a dynamic minimum number of hours required for bookings during
            specific days and time windows. Airport bookings are always exempt.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((data) => {
              setValidationError(null)
              mutation.mutate(data)
            })}
            className="space-y-4"
          >
            {validationError && (
              <div className="flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{validationError}</span>
              </div>
            )}

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Saturday Evening Minimum" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="e.g., 5-hour minimum on weekend evenings"
                      className="resize-none"
                      rows={2}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="minimum_hours"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Minimum Hours</FormLabel>
                  <FormControl>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={0.5}
                        max={24}
                        step={0.5}
                        className="w-24"
                        {...field}
                      />
                      <span className="text-muted-foreground">hours</span>
                    </div>
                  </FormControl>
                  <FormDescription>
                    Minimum number of hours required for booking
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="all_day"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>All Day</FormLabel>
                    <FormDescription className="text-xs">
                      Apply this rule for the entire day
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {!allDay && (
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="start_time"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Start Time</FormLabel>
                      <FormControl>
                        <Input type="time" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="end_time"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>End Time</FormLabel>
                      <FormControl>
                        <Input type="time" {...field} />
                      </FormControl>
                      <FormDescription className="text-xs">
                        Can be before start for overnight
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            <FormField
              control={form.control}
              name="days_of_week"
              render={() => (
                <FormItem>
                  <FormLabel>Days of Week</FormLabel>
                  <div className="flex flex-wrap gap-2">
                    {DAYS_OF_WEEK.map((day) => (
                      <label
                        key={day.value}
                        className="flex items-center gap-1.5 cursor-pointer"
                      >
                        <Checkbox
                          checked={selectedDays.includes(day.value)}
                          onCheckedChange={(checked) =>
                            handleDayToggle(day.value, checked === true)
                          }
                        />
                        <span className="text-sm">{day.label}</span>
                      </label>
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="start_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Date (optional)</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormDescription className="text-xs">
                      Leave empty for recurring
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="end_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>End Date (optional)</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Vehicle Selection */}
            <FormField
              control={form.control}
              name="apply_to_all_vehicles"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>Apply to All Vehicles</FormLabel>
                    <FormDescription className="text-xs">
                      Or select specific vehicles this rule applies to
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {!applyToAllVehicles && (
              <FormField
                control={form.control}
                name="vehicle_ids"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Select Vehicles</FormLabel>
                    <div className="border rounded-lg p-3 max-h-[150px] overflow-y-auto space-y-2">
                      {vehicles.length > 0 ? (
                        vehicles.map((vehicle) => (
                          <div key={vehicle.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`mha-vehicle-${vehicle.id}`}
                              checked={field.value?.includes(vehicle.id)}
                              onCheckedChange={(checked) => {
                                const newValue = checked
                                  ? [...(field.value || []), vehicle.id]
                                  : (field.value || []).filter((id) => id !== vehicle.id)
                                field.onChange(newValue)
                              }}
                            />
                            <label
                              htmlFor={`mha-vehicle-${vehicle.id}`}
                              className="text-sm font-medium leading-none cursor-pointer"
                            >
                              {vehicle.name}
                            </label>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          No vehicles available
                        </p>
                      )}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="priority"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Priority</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={0}
                      className="w-24"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription className="text-xs">
                    Lower number = higher priority (0 is highest)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>Active</FormLabel>
                    <FormDescription className="text-xs">
                      Enable this minimum hour rule
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {isEditing ? 'Save Changes' : 'Create Rule'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
